import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ArtistAvatarProps {
  artistName: string;
  artistId?: string;
  imageUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  showName?: boolean;
  showGenre?: boolean;
  genre?: string;
  clickable?: boolean;
  className?: string;
}

const ArtistAvatar: React.FC<ArtistAvatarProps> = ({
  artistName,
  artistId,
  imageUrl,
  size = 'md',
  showName = true,
  showGenre = false,
  genre,
  clickable = true,
  className = ''
}) => {
  const navigate = useNavigate();

  // Size classes for different avatar sizes
  const sizeClasses = {
    sm: {
      avatar: 'w-8 h-8',
      text: 'text-xs',
      nameText: 'text-sm',
      genreText: 'text-xs'
    },
    md: {
      avatar: 'w-10 h-10',
      text: 'text-sm',
      nameText: 'text-sm',
      genreText: 'text-xs'
    },
    lg: {
      avatar: 'w-12 h-12',
      text: 'text-base',
      nameText: 'text-base',
      genreText: 'text-sm'
    }
  };

  const currentSize = sizeClasses[size];

  const handleClick = (e: React.MouseEvent) => {
    if (!clickable || !artistId) return;
    
    e.preventDefault();
    e.stopPropagation();
    navigate(`/artist/${artistId}`);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const avatarElement = (
    <>
      {/* Avatar image or fallback */}
      {imageUrl ? (
        <img 
          src={imageUrl}
          alt={artistName}
          className={`${currentSize.avatar} rounded-full object-cover ring-2 ring-white shadow-md`}
          onError={(e) => {
            // Hide broken image and show fallback
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
      ) : null}
      <div 
        className={`${currentSize.avatar} rounded-full bg-gradient-to-br from-coral-pink to-coral-orange flex items-center justify-center text-white font-bold shadow-md ring-2 ring-white ${imageUrl ? 'hidden' : 'flex'}`}
      >
        <span className={currentSize.text}>
          {getInitials(artistName) || 'A'}
        </span>
      </div>

      {/* Artist name and genre */}
      {showName && (
        <div className="flex-1 min-w-0">
          <p className={`${currentSize.nameText} font-medium text-gray-900 truncate group-hover/artist:text-coral-pink transition-colors`}>
            {artistName}
          </p>
          {showGenre && genre && (
            <p className={`${currentSize.genreText} text-gray-500 truncate`}>
              {genre}
            </p>
          )}
        </div>
      )}
    </>
  );

  // If clickable and has artistId, wrap in link
  if (clickable && artistId) {
    return (
      <a
        href={`/artist/${artistId}`}
        onClick={handleClick}
        className={`flex items-center gap-2 rounded-lg hover:bg-gray-50 transition-colors group/artist ${className}`}
      >
        {avatarElement}
      </a>
    );
  }

  // Otherwise just return the avatar elements
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {avatarElement}
    </div>
  );
};

export default ArtistAvatar;